import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { DatePipe, LowerCasePipe } from '@angular/common';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { EcabInputComponent, EcabSelectComponent, EcabButtonComponent } from '@ecab/ecab-ui';
import {
  ApiClient,
  BuildingMode,
  CaseType,
  MessageDto,
  MessageDtoQuestionType,
  MyEcabTownGroupDto,
  QuestionType,
  Pageable,
  PageMessageDto,
  PredefinedMessageFilterDto,
  PredefinedMessageFilterDtoTargetView
} from '../services/api.clients.generated';
import { NewMessageModalComponent } from './new-message-modal/new-message-modal.component';
import {
  buildMessageFilterQueryParams,
  emptyMessageFilterState,
  inputValueToValues,
  readExplicitMessageFilterOverrides,
  readFilterId,
  readMessageFilterState,
  toMessagesBuildingMode,
  toMessagesPersonMode,
  valuesToInputValue
} from './message-filter-state';
import { NotificationService } from '../notifications/notification.service';
import { MarkdownExcerptPipe } from '../shared/markdown/markdown-excerpt.pipe';
import { getContactAvatarColor, getContactInitials } from './contact-display.utils';

@Component({
  selector: 'app-messages',
  imports: [
    DatePipe,
    MatPaginator,
    TranslatePipe,
    LowerCasePipe,
    FormsModule,
    MatExpansionModule,
    EcabInputComponent,
    EcabSelectComponent,
    EcabButtonComponent,
    MatIconModule,
    NewMessageModalComponent,
    MarkdownExcerptPipe
  ],
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.scss'
})
export class MessagesComponent implements OnInit, OnDestroy {
  messages: MessageDto[] = [];
  totalElements: number = 0;
  pageSize: number = 20;
  pageIndex: number = 0;
  searchTerm: string = '';

  isCreateModalOpen = false;
  predefinedFilters = signal<PredefinedMessageFilterDto[]>([]);
  townGroupOptions: { value: string; label: string }[] = [];
  advancedFiltersSectionExpanded = false;

  // Filter properties
  buildingIdFilter: string = '';
  buildingMode: BuildingMode = BuildingMode.ALL;
  caseIdFilter: string = '';
  townGroupIds: string[] = [];
  personIdFilter: string = '';
  personMode: BuildingMode = BuildingMode.ALL;
  caseTypeFilter: CaseType | undefined = undefined;
  questionTypeFilter?: MessageDtoQuestionType;

  // Expose CaseType enum to template
  CaseType = CaseType;
  FilterMode = BuildingMode;
  QuickFilterTarget = PredefinedMessageFilterDtoTargetView;
  MessageQuestionType = MessageDtoQuestionType;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private currentQueryParamMap: ParamMap;

  constructor(
    private readonly messageService: ApiClient,
    protected readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService
  ) {
    this.currentQueryParamMap = this.route.snapshot.queryParamMap;
  }

  ngOnInit() {
    this.townGroupOptions = this.buildTownGroupOptions([]);
    void this.loadPredefinedFilters();
    void this.loadTownGroups();

    this.route.queryParamMap.subscribe(params => {
      this.currentQueryParamMap = params;
      this.syncStateFromRoute();
    });

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.pageIndex = 0;
      this.updateRouteWithFilters(true);
    });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadMessages();
  }

  onSearchChange() {
    this.searchSubject.next(this.searchTerm);
  }

  loadMessages() {
    const explicitOverrides = readExplicitMessageFilterOverrides(this.currentQueryParamMap);
    const filterId = readFilterId(this.currentQueryParamMap);

    firstValueFrom(this.messageService.getInternalMessages(
      filterId,
      explicitOverrides.search,
      explicitOverrides.buildingIds,
      explicitOverrides.buildingMode ? toMessagesBuildingMode(explicitOverrides.buildingMode) : undefined,
      explicitOverrides.caseId,
      explicitOverrides.townGroupIds,
      explicitOverrides.personIds,
      explicitOverrides.personMode ? toMessagesPersonMode(explicitOverrides.personMode) : undefined,
      explicitOverrides.caseType,
      convertMessageQuestionType(explicitOverrides.questionType),
      new Pageable({
        page: this.pageIndex,
        size: this.pageSize,
        sort: ['date,desc']
      })
    )).then(
      (response: PageMessageDto) => {
        this.messages = response.content ?? [];
        this.totalElements = response.totalElements ?? 0;
      },
      error => {
        this.notificationService.error(
          this.notificationService.errorMessage(error, this.translateService.instant('message.feedback.loadError'))
        );
        this.messages = [];
        this.totalElements = 0;
      }
    );
  }

  get searchSectionSummary(): string {
    const value = this.searchTerm.trim();
    return value
      ? this.translateService.instant('filter.workspace.searchSummaryValue', { value })
      : this.translateService.instant('filter.workspace.searchSummaryEmpty');
  }

  get advancedFiltersSectionSummary(): string {
    const summaries: string[] = [];

    if (this.buildingMode !== BuildingMode.ALL) {
      summaries.push(this.translateService.instant('filter.workspace.summary.buildingMode'));
    }
    if (this.buildingMode === BuildingMode.SELECTED_VALUES && inputValueToValues(this.buildingIdFilter).length > 0) {
      summaries.push(this.translateService.instant('filter.workspace.summary.buildingValues', {
        count: inputValueToValues(this.buildingIdFilter).length
      }));
    }
    if (this.caseIdFilter.trim()) {
      summaries.push(this.translateService.instant('filter.workspace.summary.caseId'));
    }
    if (this.townGroupIds.length > 0) {
      summaries.push(this.translateService.instant('filter.workspace.summary.townGroupId'));
    }
    if (this.personMode !== BuildingMode.ALL) {
      summaries.push(this.translateService.instant('filter.workspace.summary.personMode'));
    }
    if (this.personMode === BuildingMode.SELECTED_VALUES && inputValueToValues(this.personIdFilter).length > 0) {
      summaries.push(this.translateService.instant('filter.workspace.summary.personValues', {
        count: inputValueToValues(this.personIdFilter).length
      }));
    }
    if (this.caseTypeFilter !== undefined) {
      summaries.push(this.translateService.instant('filter.workspace.summary.caseType'));
    }
    if (this.questionTypeFilter !== undefined) {
      summaries.push(this.translateService.instant('filter.workspace.summary.questionType'));
    }

    if (summaries.length === 0) {
      return this.translateService.instant('filter.workspace.advancedSummaryEmpty');
    }

    return summaries.join(' · ');
  }

  applyFilters() {
    this.pageIndex = 0;
    this.updateRouteWithFilters();
  }

  clearFilters() {
    this.pageIndex = 0;
    const emptyState = emptyMessageFilterState();
    this.buildingIdFilter = '';
    this.buildingMode = emptyState.buildingMode;
    this.caseIdFilter = '';
    this.townGroupIds = [];
    this.personIdFilter = '';
    this.personMode = emptyState.personMode;
    this.caseTypeFilter = undefined;
    this.questionTypeFilter = undefined;
    this.updateRouteWithFilters();
  }

  onBuildingModeChange() {
    if (this.buildingMode !== BuildingMode.SELECTED_VALUES) {
      this.buildingIdFilter = '';
    }
  }

  onPersonModeChange() {
    if (this.personMode !== BuildingMode.SELECTED_VALUES) {
      this.personIdFilter = '';
    }
  }

  openMessage(msg: MessageDto) {
    this.router.navigate(['/messages', msg.id], {
      state: { message: msg }
    });
  }

  setSectionExpanded(section: 'advanced', expanded: boolean) {
    this.advancedFiltersSectionExpanded = expanded;
  }

  getInitials(msg: MessageDto): string {
    return getContactInitials(msg);
  }

  getContactDisplay(msg: MessageDto): string {
    if (msg.firstname && msg.lastname) {
      return `${msg.firstname} ${msg.lastname}`;
    }
    return msg.companyName || msg.email || this.translateService.instant('common.unknown');
  }

  getAvatarColor(msg: MessageDto): string {
    return getContactAvatarColor(msg);
  }

  getQuestionTypeOptions() {
    return [
      { value: MessageDtoQuestionType.MY_ACCOUNT, label: this.translateService.instant('message.questionType.ACCOUNT') },
      { value: MessageDtoQuestionType.GENERAL_QUESTION, label: this.translateService.instant('message.questionType.GENERAL') },
      { value: MessageDtoQuestionType.CLAIM, label: this.translateService.instant('message.questionType.CLAIM') },
      { value: MessageDtoQuestionType.PREVENTION, label: this.translateService.instant('message.questionType.PREVENTION') },
      { value: MessageDtoQuestionType.ESTIMATION, label: this.translateService.instant('message.questionType.ESTIMATION') },
      { value: MessageDtoQuestionType.BUILDINGS, label: this.translateService.instant('message.questionType.BUILDING') },
      { value: MessageDtoQuestionType.SUBSIDIES, label: this.translateService.instant('message.questionType.SUBSIDY') }
    ];
  }

  createNewMessage() {
    this.isCreateModalOpen = true;
  }

  closeCreateModal() {
    this.isCreateModalOpen = false;
  }

  onMessageCreated() {
    this.isCreateModalOpen = false;
    this.notificationService.success(this.translateService.instant('message.feedback.createSuccess'));
    this.loadMessages();
  }

  private async loadTownGroups() {
    try {
      const groups = await firstValueFrom(
        this.messageService.getEstimationTownGroups(this.translateService.currentLang || undefined)
      );
      this.townGroupOptions = this.buildTownGroupOptions(groups ?? []);
    } catch (error) {
      this.notificationService.warning(
        this.notificationService.errorMessage(error, this.translateService.instant('message.feedback.townGroupsUnavailable'))
      );
      this.townGroupOptions = this.buildTownGroupOptions([]);
    }
  }

  private buildTownGroupOptions(groups: MyEcabTownGroupDto[]): { value: string; label: string }[] {
    return groups
      .filter(group => group.id !== undefined && group.id !== null)
      .map(group => ({ value: String(group.id), label: group.name ?? String(group.id) }));
  }

  private async loadPredefinedFilters() {
    try {
      const filters = await firstValueFrom(this.messageService.getPredefinedMessageFilters());
      this.predefinedFilters.set(filters ?? []);
    } catch (error) {
      this.notificationService.warning(
        this.notificationService.errorMessage(error, this.translateService.instant('message.feedback.predefinedFiltersUnavailable'))
      );
      this.predefinedFilters.set([]);
    }
    this.syncStateFromRoute();
  }

  private updateRouteWithFilters(replaceUrl = false) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildMessageFilterQueryParams({
        search: this.searchTerm.trim(),
        buildingMode: this.buildingMode,
        buildingIds: this.buildingMode === BuildingMode.SELECTED_VALUES ? inputValueToValues(this.buildingIdFilter) : [],
        caseId: this.caseIdFilter.trim(),
        townGroupIds: this.townGroupIds,
        personMode: this.personMode,
        personIds: this.personMode === BuildingMode.SELECTED_VALUES ? inputValueToValues(this.personIdFilter) : [],
        caseType: this.caseTypeFilter,
        questionType: this.questionTypeFilter
      }),
      replaceUrl
    });
  }

  private syncStateFromRoute() {
    const state = readMessageFilterState(
      this.currentQueryParamMap,
      this.predefinedFilters(),
      PredefinedMessageFilterDtoTargetView.MESSAGES
    );
    this.searchTerm = state.search;
    this.buildingMode = state.buildingMode;
    this.buildingIdFilter = valuesToInputValue(state.buildingIds);
    this.caseIdFilter = state.caseId;
    this.townGroupIds = state.townGroupIds;
    this.personMode = state.personMode;
    this.personIdFilter = valuesToInputValue(state.personIds);
    this.caseTypeFilter = state.caseType;
    this.questionTypeFilter = state.questionType;
    this.loadMessages();
  }
}

function convertMessageQuestionType(value?: MessageDtoQuestionType): QuestionType | undefined {
  if (!value) {
    return undefined;
  }
  return QuestionType[value as keyof typeof QuestionType];
}
